import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_TYPE_LABELS,
  type Appointment,
  appointmentClientName,
} from 'services/types/appointment';
import { NAVY, RADIUS_ITEM } from 'styles/tokens';

import { STATUS_STYLES, TYPE_STYLES } from './serviceCalendar.constants';
import { formatTime, formatTimeRange } from './serviceCalendar.utils';

const carLine = (a: Appointment): string =>
  [
    [a.car?.brand, a.car?.model].filter(Boolean).join(' '),
    a.car?.licensePlate,
    a.car?.km != null ? `${a.car.km.toLocaleString('es-CL')} km` : null,
    a.maintenanceInterval,
  ]
    .filter(Boolean)
    .join(' · ');

interface Props {
  appointment: Appointment;
  variant: 'timeline' | 'compact' | 'list';
  onClick?: (appointment: Appointment) => void;
}

/** Single appointment block used across the day/week timeline, the month grid and the day summary. */
const AppointmentCard = ({ appointment, variant, onClick }: Props) => {
  const typeStyle = TYPE_STYLES[appointment.type];
  const statusStyle = STATUS_STYLES[appointment.status];
  const car = carLine(appointment);

  if (variant === 'compact') {
    // Month cell pill — single line, dot + name.
    return (
      <Box
        onClick={
          onClick
            ? (e) => {
                e.stopPropagation();
                onClick(appointment);
              }
            : undefined
        }
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.75,
          py: 0.25,
          borderRadius: RADIUS_ITEM,
          backgroundColor: typeStyle.surface,
          cursor: onClick ? 'pointer' : 'default',
          overflow: 'hidden',
          transition: 'filter 0.15s ease',
          '&:hover': onClick ? { filter: 'brightness(0.97)' } : undefined,
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: typeStyle.rail, flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: NAVY,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {formatTime(appointment.date)} {appointmentClientName(appointment)}
        </Typography>
      </Box>
    );
  }

  const isList = variant === 'list';

  return (
    <Box
      onClick={onClick ? () => onClick(appointment) : undefined}
      sx={{
        position: 'relative',
        height: variant === 'timeline' ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        pl: 1.25,
        pr: 1,
        py: isList ? 1.25 : 0.75,
        borderRadius: RADIUS_ITEM,
        backgroundColor: isList ? '#fff' : typeStyle.surface,
        boxShadow: isList ? '0px 1px 3px rgba(21, 24, 58, 0.08)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        transition: 'filter 0.15s ease, box-shadow 0.2s ease',
        '&:hover': onClick ? { boxShadow: '0px 4px 14px rgba(21, 24, 58, 0.12)' } : undefined,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 6,
          bottom: 6,
          width: 3,
          borderRadius: 2,
          backgroundColor: typeStyle.rail,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: NAVY,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {appointmentClientName(appointment)}
        </Typography>
        {isList && (
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'rgba(21, 24, 58, 0.55)', flexShrink: 0 }}>
            {formatTimeRange(appointment)}
          </Typography>
        )}
      </Box>

      <Typography sx={{ fontSize: 11.5, color: 'rgba(21, 24, 58, 0.65)', lineHeight: 1.2 }}>
        {!isList && `${formatTime(appointment.date)} · `}
        {APPOINTMENT_TYPE_LABELS[appointment.type]}
        {car ? ` · ${car}` : ''}
      </Typography>

      {isList && (
        <Box
          sx={{
            mt: 0.25,
            alignSelf: 'flex-start',
            px: 0.75,
            py: 0.1,
            borderRadius: RADIUS_ITEM,
            backgroundColor: statusStyle.surface,
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: statusStyle.color,
            }}
          >
            {APPOINTMENT_STATUS_LABELS[appointment.status]}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AppointmentCard;
